import parsePhoneNumber from "libphonenumber-js";
import {
  Building2,
  ChevronDown,
  ChevronRight,
  GlobeIcon,
  MapPin,
  Phone,
  Tag,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "../../ui/button";

import type { Company } from "@cohesive/api-entities";
import {
  BaseEntityCard,
  BaseEntityCardActions,
  BaseEntityCardBody,
  BaseEntityCardContent,
  BaseEntityCardContextMenu,
  BaseEntityCardExternalLinkAction,
  BaseEntityCardHeader,
  BaseEntityCardIcon,
  BaseEntityCardMentionAction,
  BaseEntityCardMetadata,
  BaseEntityCardMetadataItem,
  BaseEntityCardProps,
  BaseEntityCardTitle,
} from "../BaseCard";

export { Company };

type CompanyCardProps = Company &
  BaseEntityCardProps & {
    custom_properties?: Record<string, string>;
  };

// Avatar component for CompanyCard
export function CompanyCardAvatar({
  className,
  style,
}: Partial<CompanyCardProps>) {
  return (
    <BaseEntityCardIcon className={className} style={style}>
      <Building2 className="h-6 w-6" />
    </BaseEntityCardIcon>
  );
}

export function CompanyCard({
  platform,
  name,
  industry,
  city,
  state,
  phone,
  website,
  web_link,
  onClick,
  onDoubleClick,
  onMentionClick,
  actions,
  processes,
  className,
  style,
  custom_properties,
}: CompanyCardProps) {
  const [showCustomProperties, setShowCustomProperties] = useState(false);

  const formattedPhone = useMemo(() => {
    if (!phone) return null;
    const parsed = parsePhoneNumber(phone);
    if (!parsed) return null;
    return parsed.formatInternational();
  }, [phone]);

  const location = useMemo(() => {
    if (city && state) return `${city}, ${state}`;
    if (city) return city;
    if (state) return state;
    return null;
  }, [city, state]);

  const hasCustomProperties = useMemo(() => {
    return custom_properties && Object.keys(custom_properties).length > 0;
  }, [custom_properties]);

  const formatPropertyName = (propName: string) => {
    return propName.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  return (
    <BaseEntityCard
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      processes={processes}
      className={className}
      style={style}
    >
      <CompanyCardAvatar />

      <BaseEntityCardContent>
        <BaseEntityCardHeader>
          <BaseEntityCardTitle platform={platform}>{name}</BaseEntityCardTitle>
          <BaseEntityCardActions>
            {onMentionClick && (
              <BaseEntityCardMentionAction onClick={onMentionClick} />
            )}
            {web_link && <BaseEntityCardExternalLinkAction href={web_link} />}
            {actions && actions.length > 0 && (
              <BaseEntityCardContextMenu actions={actions} />
            )}
          </BaseEntityCardActions>
        </BaseEntityCardHeader>

        <BaseEntityCardBody>
          <BaseEntityCardMetadata>
            {industry && (
              <BaseEntityCardMetadataItem
                icon={<Building2 className="h-4 w-4" />}
              >
                {industry.replace(/_/g, " ").toLowerCase()}
              </BaseEntityCardMetadataItem>
            )}
            {location && (
              <BaseEntityCardMetadataItem icon={<MapPin className="h-4 w-4" />}>
                {location}
              </BaseEntityCardMetadataItem>
            )}
            {website && (
              <BaseEntityCardMetadataItem
                icon={<GlobeIcon className="h-4 w-4" />}
              >
                {website}
              </BaseEntityCardMetadataItem>
            )}
            {(formattedPhone || phone) && (
              <BaseEntityCardMetadataItem icon={<Phone className="h-4 w-4" />}>
                {formattedPhone || phone}
              </BaseEntityCardMetadataItem>
            )}
          </BaseEntityCardMetadata>

          {/* Collapsible Custom Properties Section */}
          {hasCustomProperties && (
            <div className="flex flex-col gap-1.5 mt-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-5 p-0 text-xs text-muted-foreground hover:text-foreground justify-start -ml-1"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowCustomProperties(!showCustomProperties);
                }}
              >
                {showCustomProperties ? (
                  <ChevronDown className="h-3 w-3 mr-1" />
                ) : (
                  <ChevronRight className="h-3 w-3 mr-1" />
                )}
                <Tag className="h-3 w-3 mr-1" />
                {Object.keys(custom_properties!).length} custom{" "}
                {Object.keys(custom_properties!).length === 1
                  ? "property"
                  : "properties"}
              </Button>

              {showCustomProperties && (
                <div className="mt-1 space-y-1 pl-1">
                  {Object.entries(custom_properties!).map(([key, value]) => (
                    <div key={key} className="flex items-start gap-2 text-xs">
                      <span className="text-muted-foreground font-medium min-w-0 truncate">
                        {formatPropertyName(key)}:
                      </span>
                      <span className="text-foreground break-words min-w-0">
                        {value || "—"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </BaseEntityCardBody>
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}
